import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as authApi from "../../api/auth";
import * as usersApi from "../../api/users";
import { TOKEN_STORAGE_KEY, getErrorMessage } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { Logo } from "../../components/ui/Logo";

interface RegisterForm {
  full_name: string;
  riferimento: string;
  password: string;
  confirm_password: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  centro_assistenza: "Centro Assistenza",
  istituto_vigilanza: "Istituto di Vigilanza",
};

export default function Register() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();
  const { setUser, setPermissions } = useAuth();
  const { showToast } = useToast();

  const [status, setStatus] = useState<"verifying" | "valid" | "invalid">("verifying");
  const [invitoInfo, setInvitoInfo] = useState<{ email: string; role: string } | null>(null);
  const [invalidMessage, setInvalidMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setInvalidMessage("Link di invito mancante o incompleto.");
      return;
    }

    usersApi
      .verifyInvito(token)
      .then((info) => {
        setInvitoInfo(info);
        setStatus("valid");
      })
      .catch((err) => {
        setInvalidMessage(getErrorMessage(err, "Invito non valido."));
        setStatus("invalid");
      });
  }, [token]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>();

  // Coerente con la policy lato server (passwordPolicy.ts): admin/staff
  // richiedono almeno 12 caratteri, gli altri ruoli 8.
  const minPasswordLength = invitoInfo?.role === "admin" || invitoInfo?.role === "staff" ? 12 : 8;

  const onSubmit = handleSubmit(async ({ full_name, riferimento, password }) => {
    try {
      const { token: jwtToken, user, permissions } = await authApi.register({
        token,
        password,
        full_name,
        riferimento,
      });
      localStorage.setItem(TOKEN_STORAGE_KEY, jwtToken);
      setUser(user);
      setPermissions(permissions);
      showToast("Registrazione completata. Benvenuto su Wink on Support!", "success");
      navigate("/", { replace: true });
    } catch (err) {
      showToast(getErrorMessage(err, "Registrazione non riuscita. Riprova."), "error");
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="w-full max-w-sm rounded-card bg-white p-8 shadow-card">
        <div className="mb-6 text-center">
          <Logo className="mx-auto w-44" />
          <p className="mt-2 text-sm text-ink/50">Completa la registrazione</p>
        </div>

        {status === "verifying" && <p className="text-center text-sm text-ink/50">Verifica dell'invito in corso…</p>}

        {status === "invalid" && (
          <div className="rounded-lg bg-accent/10 px-4 py-3 text-sm text-accent">{invalidMessage}</div>
        )}

        {status === "valid" && invitoInfo && (
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <div className="rounded-lg bg-primary/5 px-4 py-3 text-sm">
              <p className="text-ink/70">
                Account: <span className="font-semibold text-ink">{invitoInfo.email}</span>
              </p>
              <p className="text-ink/70">
                Ruolo: <span className="font-semibold text-ink">{ROLE_LABELS[invitoInfo.role] ?? invitoInfo.role}</span>
              </p>
            </div>

            {/* Nome facoltativo: un'azienda può registrarsi con una casella
                generica (info@, amministrazione@…) che non appartiene a una
                persona sola. Dove manca, l'interfaccia mostra l'email — il
                fallback esiste già ovunque. */}
            <Input
              label="Nome e cognome"
              hint="Facoltativo — lascialo vuoto se l'account è di un'azienda e non di una persona."
              error={errors.full_name?.message}
              {...register("full_name")}
            />

            {/* Serve a distinguere più account della stessa azienda quando la
                casella è generica e il nome resta vuoto. */}
            <Input
              label="Riferimento o reparto"
              hint="Facoltativo — es. Amministrazione, Sede di Milano."
              error={errors.riferimento?.message}
              {...register("riferimento")}
            />

            <Input
              label="Password"
              type="password"
              required
              hint={`Almeno ${minPasswordLength} caratteri.`}
              error={errors.password?.message}
              {...register("password", {
                required: "La password è obbligatoria.",
                minLength: {
                  value: minPasswordLength,
                  message: `La password deve contenere almeno ${minPasswordLength} caratteri.`,
                },
              })}
            />

            <Input
              label="Conferma password"
              type="password"
              required
              error={errors.confirm_password?.message}
              {...register("confirm_password", {
                required: "Conferma la password.",
                validate: (value) => value === watch("password") || "Le due password non coincidono.",
              })}
            />

            <Button type="submit" loading={isSubmitting} className="mt-2 w-full">
              Completa la registrazione
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
